import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Link as RouterLink, Navigate, useSearchParams } from 'react-router-dom'
import { usePanelAccess } from '../permissions/usePanelAccess'
import {
  createUser,
  deleteUser,
  formatUserDate,
  formatUserDateTime,
  getUserById,
  listUsers,
  perfilLabel,
  resendInvite,
  updateUser,
  type BiUser,
  type UserFilters,
  type UserFormData,
} from '../api/users'
import { WHATSAPP_TAB_TARGET, whatsappOpenUrl } from '../utils/whatsapp'
import { DeleteConfirmationDialog } from '../components/DeleteConfirmationDialog'
import { Hicon } from '../components/Hicon'
import { UserForm } from '../components/UserForm'
import { useBiAuth } from '../state/BiAuthContext'

export function UsuariosPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user: authUser, updateUser: updateAuthUser } = useBiAuth()
  const { isAdmin } = usePanelAccess()
  const [users, setUsers] = useState<BiUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 10,
    search: '',
    ativo: undefined,
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  })
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState('')
  const [expandedEditUserId, setExpandedEditUserId] = useState<string | null>(null)
  const [editUser, setEditUser] = useState<BiUser | null>(null)
  const [editUserLoading, setEditUserLoading] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  /** Convite criado com WhatsApp: fica visível até o admin enviar ou fechar. */
  const [whatsappInvite, setWhatsappInvite] = useState<{
    nome: string
    url: string
    viaEmail: boolean
  } | null>(null)
  /** Reenvio: e-mail enviado; pergunta se manda também no WhatsApp (mesmo link). */
  const [reenvioDialog, setReenvioDialog] = useState<{
    nome: string
    email: string
    url: string
  } | null>(null)
  // Aberto/fechado à parte dos dados: o link do "Sim" não pode sumir no clique.
  const [reenvioOpen, setReenvioOpen] = useState(false)
  const inviteAlertRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (whatsappInvite) {
      inviteAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [whatsappInvite])
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    user: null as BiUser | null,
    loading: false,
  })

  const usersRef = useRef(users)
  usersRef.current = users

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const response = await listUsers(filters)
      setUsers(response.usuarios)
      setPagination(response.paginacao)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuários')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void loadUsers()
  }, [loadUsers])

  useEffect(() => {
    setExpandedEditUserId(null)
    setEditUser(null)
    setEditError('')
  }, [filters.page, filters.limit])

  const openEditById = useCallback(async (id: string, listRow?: BiUser | null) => {
    if (!id.trim()) return
    const trimmed = id.trim()
    setShowCreateForm(false)
    setCreateError('')
    setExpandedEditUserId(trimmed)
    setEditError('')
    const fromList =
      listRow && listRow.id === trimmed
        ? listRow
        : usersRef.current.find((u) => u.id === trimmed) ?? null
    if (fromList) {
      setEditUser(fromList)
      setEditUserLoading(false)
    } else {
      setEditUser(null)
      setEditUserLoading(true)
    }
    try {
      const fresh = await getUserById(trimmed)
      setEditUser(fresh)
    } catch (err: unknown) {
      if (fromList) {
        setEditError(
          err instanceof Error
            ? err.message
            : 'Não foi possível sincronizar com o servidor. Os dados da lista serão usados.',
        )
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao carregar usuário')
        setExpandedEditUserId(null)
        setEditUser(null)
      }
    } finally {
      setEditUserLoading(false)
    }
  }, [])

  useEffect(() => {
    const id = searchParams.get('edit')
    if (!id?.trim()) return
    const next = new URLSearchParams(searchParams)
    next.delete('edit')
    setSearchParams(next, { replace: true })
    void openEditById(id)
  }, [searchParams, setSearchParams, openEditById])

  const handleToggleCreateForm = () => {
    setShowCreateForm((prev) => {
      const next = !prev
      if (next) {
        setExpandedEditUserId(null)
        setEditUser(null)
        setEditError('')
      }
      return next
    })
    setCreateError('')
  }

  const handleCreateUser = async (formData: UserFormData) => {
    try {
      setCreateLoading(true)
      setCreateError('')
      const created = await createUser({
        nome: formData.nome,
        email: formData.email,
        ...(formData.senha ? { senha: formData.senha } : {}),
        ...(formData.whatsapp ? { whatsapp: formData.whatsapp } : {}),
        tipo: formData.tipo,
        ativo: formData.ativo,
        paineis: formData.paineis,
      })
      if (created.whatsappUrl) {
        setWhatsappInvite({ nome: created.user.nome, url: created.whatsappUrl, viaEmail: true })
      }
      setSuccessMessage(
        created.message ||
          (created.conviteEnviado
            ? 'Utilizador criado. Enviámos um e-mail para definir a senha.'
            : 'Usuário criado com sucesso!'),
      )
      setShowCreateForm(false)
      await loadUsers()
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Erro ao criar usuário')
    } finally {
      setCreateLoading(false)
    }
  }

  const handleResendInvite = async () => {
    if (!editUser) return
    try {
      setEditError('')
      const r = await resendInvite(editUser.id)
      if (r.whatsappUrl) {
        // E-mail já foi; pergunta se manda também no WhatsApp (mesmo link).
        setReenvioDialog({ nome: editUser.nome, email: editUser.email, url: r.whatsappUrl })
        setReenvioOpen(true)
      } else {
        setSuccessMessage(r.message || 'Convite reenviado por e-mail.')
      }
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : 'Erro ao reenviar convite')
    }
  }

  const handleUpdateUser = async (formData: UserFormData) => {
    if (!editUser) return
    try {
      setEditSaving(true)
      setEditError('')
      const updated = await updateUser(editUser.id, {
        nome: formData.nome,
        email: formData.email,
        whatsapp: formData.whatsapp ?? '',
        tipo: formData.tipo,
        ativo: formData.ativo,
        paineis: formData.tipo === 'admin' ? null : formData.paineis,
        ...(formData.senha ? { senha: formData.senha } : {}),
      })
      if (authUser?.id === editUser.id) {
        updateAuthUser({
          id: updated.id,
          email: updated.email,
          nome: updated.nome,
          tipo: updated.tipo,
          paineis: updated.paineis,
        })
      }
      setSuccessMessage('Usuário atualizado com sucesso!')
      setExpandedEditUserId(null)
      setEditUser(null)
      await loadUsers()
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : 'Erro ao atualizar usuário')
    } finally {
      setEditSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.user) return
    try {
      setDeleteDialog((prev) => ({ ...prev, loading: true }))
      await deleteUser(deleteDialog.user.id)
      await loadUsers()
      setDeleteDialog({ open: false, user: null, loading: false })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir usuário')
      setDeleteDialog((prev) => ({ ...prev, loading: false }))
    }
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1.5 }} aria-label="breadcrumb">
        <MuiLink component={RouterLink} to="/" underline="hover" color="inherit">
          Home
        </MuiLink>
        <Typography color="text.primary">Sistema</Typography>
        <Typography color="text.primary">Usuários</Typography>
      </Breadcrumbs>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Usuários do Sistema
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie os usuários que podem acessar o sistema
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Hicon name="plus" />}
          onClick={handleToggleCreateForm}
        >
          {showCreateForm ? 'Fechar formulário' : 'Adicionar Usuário'}
        </Button>
      </Box>

      <Collapse in={showCreateForm} timeout="auto" unmountOnExit>
        <Box sx={{ mb: 3 }}>
          <UserForm
            variant="create"
            onSubmit={handleCreateUser}
            onCancel={() => setShowCreateForm(false)}
            loading={createLoading}
            error={createError}
          />
        </Box>
      </Collapse>

      {whatsappInvite ? (
        <Alert
          ref={inviteAlertRef}
          severity="success"
          sx={{ mb: 2 }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                component="a"
                href={whatsappOpenUrl(whatsappInvite.url)}
                target={WHATSAPP_TAB_TARGET}
                rel="noopener noreferrer"
                size="small"
                variant="contained"
                onClick={() => setWhatsappInvite(null)}
              >
                Enviar no WhatsApp
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => setWhatsappInvite(null)}
              >
                Agora não
              </Button>
            </Box>
          }
        >
          {whatsappInvite.viaEmail
            ? `Convite de ${whatsappInvite.nome} enviado por e-mail. Quer mandar também no WhatsApp? `
            : `Link de ${whatsappInvite.nome} pronto. `}
          A mensagem já vai pronta — é só carregar em enviar.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper elevation={1}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usuário</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Perfil</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Último login</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <Fragment key={user.id}>
                  <TableRow hover selected={expandedEditUserId === user.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: 'primary.main',
                            fontSize: '0.95rem',
                            fontWeight: 700,
                          }}
                        >
                          {(user.nome || '?')[0].toUpperCase()}
                        </Avatar>
                        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                          <Typography component="span" fontWeight={600}>
                            {user.nome}
                          </Typography>
                          {authUser && user.id === authUser.id ? (
                            <Chip label="Você" size="small" color="info" variant="outlined" />
                          ) : null}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={perfilLabel(user.tipo)}
                        color={user.tipo === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.ativo ? 'Ativo' : 'Inativo'}
                        color={user.ativo ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {user.ultimo_login ? formatUserDateTime(user.ultimo_login) : 'Nunca'}
                    </TableCell>
                    <TableCell>{formatUserDate(user.data_criacao)}</TableCell>
                    <TableCell align="right">
                      <Box display="inline-flex" alignItems="center" gap={0.5}>
                        <Tooltip title="Editar">
                          <IconButton
                            onClick={() => void openEditById(user.id, user)}
                            sx={(theme) => ({
                              padding: theme.spacing(1, 2),
                              minHeight: 34,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              color: 'text.primary',
                              bgcolor: 'transparent',
                              '&:hover': { bgcolor: 'action.hover' },
                            })}
                          >
                            <Hicon name="pencil" sx={{ fontSize: '0.875rem' }} />
                          </IconButton>
                        </Tooltip>
                        {authUser && user.id === authUser.id ? null : (
                          <Tooltip title="Excluir">
                            <IconButton
                              onClick={() =>
                                setDeleteDialog({ open: true, user, loading: false })
                              }
                              sx={(theme) => ({
                                padding: theme.spacing(1, 2),
                                minHeight: 34,
                                borderRadius: 1,
                                bgcolor: 'error.main',
                                color: 'common.white',
                                '&:hover': { bgcolor: 'error.dark' },
                              })}
                            >
                              <Hicon name="trash" sx={{ fontSize: '0.875rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {expandedEditUserId === user.id ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          py: 0,
                          px: 2,
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          bgcolor: (theme) => theme.palette.action.hover,
                        }}
                      >
                        <Box sx={{ py: 2 }}>
                          {editUserLoading && !editUser ? (
                            <Box display="flex" justifyContent="center" py={2}>
                              <CircularProgress />
                            </Box>
                          ) : editUser && editUser.id === user.id ? (
                            <UserForm
                              variant="edit"
                              user={editUser}
                              onSubmit={handleUpdateUser}
                              onResendInvite={handleResendInvite}
                              onCancel={() => {
                                setExpandedEditUserId(null)
                                setEditUser(null)
                                setEditError('')
                              }}
                              loading={editSaving}
                              error={editError}
                            />
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={(_, newPage) => setFilters((prev) => ({ ...prev, page: newPage + 1 }))}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              limit: parseInt(e.target.value, 10),
              page: 1,
            }))
          }
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </Paper>

      <DeleteConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null, loading: false })}
        onConfirm={handleDeleteConfirm}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o usuário "${deleteDialog.user?.nome}"? Esta ação não pode ser desfeita.`}
        loading={deleteDialog.loading}
      />

      <Dialog
        open={reenvioOpen}
        onClose={() => setReenvioOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (t) => t.zIndex.modal + 4 }}
      >
        <DialogTitle>E-mail enviado com sucesso</DialogTitle>
        <DialogContent>
          <DialogContentText>
            O convite de {reenvioDialog?.nome} foi enviado para {reenvioDialog?.email}.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>Deseja enviar também no WhatsApp?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReenvioOpen(false)}>Não</Button>
          <Button
            component="a"
            href={reenvioDialog ? whatsappOpenUrl(reenvioDialog.url) : undefined}
            target={WHATSAPP_TAB_TARGET}
            rel="noopener noreferrer"
            variant="contained"
            onClick={() => setReenvioOpen(false)}
          >
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
